<template>
  <Teleport to="body">
    <Transition name="search-panel-fade">
      <div
        v-if="visible"
        class="search-panel-overlay"
        :style="{ paddingTop: `${headerHeight}px` }"
        @click="handleOverlayClick"
        @touchmove.prevent
      >
        <div class="search-panel-container" @click.stop @touchmove.stop>
          <button
            v-if="!isMobile"
            aria-label="关闭搜索"
            type="button"
            class="search-panel__close-btn"
            @click="handleCancel"
          >
            <icon-close />
          </button>

          <!-- 搜索输入框区域 -->
          <div class="search-panel__input-area">
            <div class="search-panel__input-wrapper">
              <button
                v-if="isMobile"
                type="button"
                class="search-panel__back-btn"
                aria-label="返回"
                @click="handleCancel"
              >
                <icon-left />
              </button>
              <div
                class="search-panel__input-container"
                @mouseenter="isInputHovered = true"
                @mouseleave="isInputHovered = false"
              >
                <a-input
                  ref="searchInputRef"
                  v-model="searchKeyword"
                  :placeholder="resolvedPlaceholder"
                  class="search-panel__input"
                  :class="{ 'search-panel__input--mobile': isMobile }"
                  :maxlength="maxLength"
                  @input="handleSearchInput"
                  @press-enter="handleSearchSubmit"
                >
                  <template #prefix>
                    <!-- 移动端：搜索图标 -->
                    <SvgIcon
                      v-if="isMobile"
                      name="search"
                      size="18"
                      class="search-panel__search-icon"
                    />
                  </template>
                  <template #suffix>
                    <!-- PC端：清除图标 -->
                    <icon-close
                      v-if="searchKeyword && (isInputHovered || isMobile)"
                      class="search-panel__clear-icon"
                      :class="{ 'search-panel__clear-icon--mobile': isMobile }"
                      @click.stop="clearSearchKeyword"
                    />
                  </template>
                </a-input>
              </div>
              <!-- 移动端：取消按钮 -->
              <a-button
                v-if="isMobile"
                type="text"
                class="search-panel__cancel-btn"
                :class="{ 'search-panel__cancel-btn--submit': !isSearchKeywordEmpty }"
                @click="handleMobileAction"
              >
                {{ mobileActionText }}
              </a-button>
              <!-- PC端：查询按钮（移动端使用键盘确认键） -->
              <a-button
                v-if="!isMobile"
                type="primary"
                class="search-panel__search-btn"
                @click="handleSearchSubmit"
              >
                查询
              </a-button>
            </div>
          </div>

          <!-- 搜索历史（输入联想态隐藏） -->
          <div v-if="!showThink && searchHistoryList.length > 0" class="search-panel__section">
            <div class="search-panel__section-title">
              <b>搜索历史</b>
              <div class="search-panel__section-title-right">
                <div
                  v-if="hasHistoryOverflow"
                  class="search-panel__expand-btn-small"
                  @click="isHistoryExpanded = !isHistoryExpanded"
                >
                  {{ isHistoryExpanded ? '收起' : '展开' }}
                </div>
                <IconDelete class="search-panel__clear-all" @click="handleClearHistory" />
              </div>
            </div>
            <div
              ref="historyTagsRef"
              class="search-panel__tags"
              :class="{ 'search-panel__tags--expanded': isHistoryExpanded }"
            >
              <base-search-tag
                v-for="(item, index) in searchHistoryList"
                :key="index"
                :text="truncateText(getHistoryText(item), 20)"
                @click="handleHistoryClick(item)"
              />
            </div>
          </div>

          <!-- 热门搜索（输入联想态隐藏） -->
          <!-- 骨架屏 -->
          <div
            v-if="!showThink && hotSearchLoading && hotSearchList.length === 0"
            class="search-panel__section"
          >
            <div class="search-panel__skeleton">
              <div class="search-panel__skeleton-title"></div>
              <div class="search-panel__skeleton-tags">
                <div v-for="i in 6" :key="i" class="search-panel__skeleton-tag"></div>
              </div>
            </div>
          </div>

          <!-- 热门搜索内容 -->
          <div v-else-if="!showThink && hotSearchList.length > 0" class="search-panel__section">
            <div class="search-panel__section-title">
              <b>热门搜索</b>
              <div
                v-if="hasHotOverflow"
                class="search-panel__expand-btn-small"
                @click="isHotSearchExpanded = !isHotSearchExpanded"
              >
                {{ isHotSearchExpanded ? '收起' : '展开' }}
              </div>
            </div>
            <div
              ref="hotTagsRef"
              class="search-panel__tags"
              :class="{ 'search-panel__tags--expanded': isHotSearchExpanded }"
            >
              <base-search-tag
                v-for="(item, index) in hotSearchList"
                :key="index"
                :text="truncateText(getHotSearchText(item), 20)"
                @click="handleHotSearchClick(item)"
              />
            </div>
          </div>

          <!-- 搜索联想（仅商品 · 输入即联想） -->
          <div
            v-if="!isActivityMode && showThink"
            class="search-panel__section search-panel__think-list"
          >
            <div
              v-if="(thinkPending || thinkLoading) && thinkList.length === 0"
              class="search-panel__think-tip search-panel__think-tip--loading"
            >
              <a-spin :size="16" />
              <span>搜索中…</span>
            </div>
            <template v-else-if="thinkList.length > 0">
              <div
                v-for="(item, index) in thinkList"
                :key="item.serviceId || index"
                class="think-item"
                @click="handleThinkClick(item)"
              >
                <icon-search class="think-item__icon" />
                <span class="think-item__text">{{ formatThinkItemText(item) }}</span>
              </div>
            </template>
            <div v-else class="search-panel__think-tip">未找到相关商品</div>
          </div>
        </div>
      </div>
    </Transition>

    <!-- 移动端清除搜索历史确认弹窗（Figma 设计稿样式） -->
    <a-modal
      v-if="isMobile"
      v-model:visible="showClearConfirmModal"
      :align-center="true"
      :hide-title="true"
      :footer="false"
      :closable="false"
      :mask-closable="true"
      :width="270"
      :modal-style="{ borderRadius: '8px' }"
      :body-style="{ padding: 0 }"
      modal-class="search-panel-clear-confirm-modal"
      unmount-on-close
      @cancel="handleClearConfirmCancel"
    >
      <div class="search-panel-clear-confirm-modal__content">确定要清除全部搜索历史吗？</div>
      <div class="search-panel-clear-confirm-modal__footer">
        <button
          type="button"
          class="search-panel-clear-confirm-modal__btn search-panel-clear-confirm-modal__btn--cancel"
          @click="handleClearConfirmCancel"
        >
          取消
        </button>
        <button
          type="button"
          class="search-panel-clear-confirm-modal__btn search-panel-clear-confirm-modal__btn--ok"
          @click="handleClearConfirmOk"
        >
          确定
        </button>
      </div>
    </a-modal>
  </Teleport>
</template>

<script setup>
/**
 * @description 搜索弹窗组件
 * @param {boolean} visible - 控制弹窗显示/隐藏
 * @param {'product'|'activity'} [mode] - product 跳转商城搜索；activity 仅 emit search
 * @param {string} [placeholder] - 输入框占位符
 * @param {string} [initialKeyword] - 打开时预填关键词
 * @param {number} [maxLength] - 输入最大长度
 * @emits update:visible - 更新显示状态
 * @emits search - 触发搜索事件，传递搜索关键词（activity 模式；product 模式也会 navigate）
 */

import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import { IconClose, IconDelete, IconLeft, IconSearch } from '@arco-design/web-vue/es/icon'
import { BaseMessage } from '~/utils/baseMessage'
import { baseApi } from '~/api/base'
import { useAuth } from '~/composables/useAuth'
import { useStoreProductSuggestions } from '~/composables/useStoreProductSuggestions'
import { useUserStore } from '~/store/common/user'
import { useDebounceFn, useThrottleFn } from '@/composables/useDebounceThrottle'
import { useMediaQuery } from '~/composables/useMediaQuery'
import { getBaseHeaderHeightByWidth } from '~/utils/theme'
import {
  createStoreSearchPayload,
  getHotSearchDisplayText,
  getHotSearchKeyword,
  getSearchHistoryDisplayText,
  getSearchHistoryKeyword,
  mergeSearchHistoryLists,
} from '~/composables/useStoreSearchKeyword'
import CommonModal from '~/utils/commonModal'
import { STORAGE_NAMES, storageKey } from '~/utils/storageKey'
import { rememberProductEntrySource, TRACK_SOURCE_TYPE } from '~/utils/tracking/sourceContext'

const searchHistoryKey = storageKey(STORAGE_NAMES.SEARCH_HISTORY)
const isActivityMode = computed(() => props.mode === 'activity')
const resolvedPlaceholder = computed(() => {
  if (props.placeholder) return props.placeholder
  return isActivityMode.value ? '请输入商品名称/型号' : '请输入关键词查找'
})

/**
 * @description 当前模式下的搜索历史 localStorage 键
 * @returns {string}
 */
function getActiveSearchHistoryKey() {
  return isActivityMode.value
    ? storageKey(`${STORAGE_NAMES.MOBILE_SEARCH_HISTORY}_activity`)
    : searchHistoryKey
}

// Props
const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  /**
   * @description product：商城全局商品搜索；activity：营销活动页搜索（仅 emit，不跳转）
   */
  mode: {
    type: String,
    default: 'product',
    validator: (value) => ['product', 'activity'].includes(value),
  },
  placeholder: {
    type: String,
    default: '',
  },
  initialKeyword: {
    type: String,
    default: '',
  },
  maxLength: {
    type: Number,
    default: 20,
  },
})

// Emits
const emit = defineEmits(['update:visible', 'search'])

// Composables
const userStore = useUserStore()
const isLoggedIn = computed(() => userStore.isLoggedIn)
const { isBelowDesktop } = useMediaQuery()
const isMobile = isBelowDesktop

// Refs
const searchKeyword = ref('')
const searchInputRef = ref(null)
const isInputHovered = ref(false)
const hotSearchList = ref([])
const searchHistoryList = ref([])
const isHotSearchExpanded = ref(false)
const isHistoryExpanded = ref(false)
const hotTagsRef = ref(null)
const historyTagsRef = ref(null)
const hasHotOverflow = ref(false)
const hasHistoryOverflow = ref(false)
const hotSearchLoading = ref(false) // 热门搜索加载状态
const headerHeight = ref(48)
const showClearConfirmModal = ref(false) // 移动端清除搜索历史确认弹窗
const showThink = ref(false) // 输入联想态：true 时隐藏历史/热搜，展示商品联想
const {
  suggestions: thinkList,
  pending: thinkPending,
  loading: thinkLoading,
  scheduleSuggestions,
  clearSuggestions,
} = useStoreProductSuggestions()
let searchPanelOpenScrollTop = 0
const isSearchKeywordEmpty = computed(() => !searchKeyword.value.trim())
const mobileActionText = computed(() => (isSearchKeywordEmpty.value ? '取消' : '搜索'))

const getPageScrollTop = () => {
  if (!import.meta.client) return 0
  return window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0
}

const handleSearchPanelPageScroll = () => {
  if (!props.visible) return
  if (Math.abs(getPageScrollTop() - searchPanelOpenScrollTop) <= 2) return
  emit('update:visible', false)
}

const bindSearchPanelScrollClose = () => {
  if (!import.meta.client) return
  searchPanelOpenScrollTop = getPageScrollTop()
  window.addEventListener('scroll', handleSearchPanelPageScroll, { passive: true })
}

const unbindSearchPanelScrollClose = () => {
  if (!import.meta.client) return
  window.removeEventListener('scroll', handleSearchPanelPageScroll)
}

/**
 * @description 检查是否超出两行
 */
const checkOverflow = () => {
  nextTick(() => {
    if (hotTagsRef.value) {
      hasHotOverflow.value = hotTagsRef.value.scrollHeight > 64
    }
    if (historyTagsRef.value) {
      hasHistoryOverflow.value = historyTagsRef.value.scrollHeight > 64
    }
  })
}

/**
 * @description 获取header高度（resize 时使用节流版本，避免频繁计算）
 */
const getHeaderHeight = () => {
  if (!import.meta.client) return

  try {
    const headerElement = document.querySelector('.base-header')
    if (headerElement) {
      headerHeight.value = headerElement.offsetHeight
    } else {
      headerHeight.value = getBaseHeaderHeightByWidth(window.innerWidth)
    }
  } catch (error) {
    console.error('获取header高度失败:', error)
    // 失败时使用默认值
    headerHeight.value = getBaseHeaderHeightByWidth(window.innerWidth)
  }
}
const throttledGetHeaderHeight = useThrottleFn(getHeaderHeight, 150)

/**
 * @description 输入联想（防抖 300ms）；限制输入长度为 20 字
 */
const handleSearchInput = () => {
  const limit = Math.max(1, Number(props.maxLength) || 20)
  if (searchKeyword.value && searchKeyword.value.length > limit) {
    searchKeyword.value = searchKeyword.value.slice(0, limit)
  }
  if (isActivityMode.value) return

  const keyword = searchKeyword.value?.trim()
  if (!keyword) {
    showThink.value = false
    clearSuggestions()
    return
  }
  showThink.value = true
  scheduleSuggestions(keyword)
}

/**
 * @description 格式化联想项展示文本：型号 + 空格 + 名称
 * @param {Record<string, any>} item - 商品联想项
 * @returns {string} 联想展示文本
 */
const formatThinkItemText = (item) => {
  const modelNo = item?.modelNo?.trim() || ''
  const name = item?.serviceName?.trim() || ''
  if (modelNo && name) return `${modelNo} ${name}`
  return modelNo || name
}

/**
 * @description 处理联想词点击：直达商品详情
 */
const handleThinkClick = (item) => {
  const serviceId = item?.value ?? item?.serviceId ?? item?.id
  if (serviceId == null) return
  const keyword = String(searchKeyword.value || item?.label || item?.serviceName || '').trim()
  rememberProductEntrySource(serviceId, {
    sourceType: TRACK_SOURCE_TYPE.SEARCH_RESULT,
    sourceId: keyword || 'search_result',
    sourceName: keyword || '搜索结果',
    fromPage: 'search_result',
  })
  emit('update:visible', false)
  navigateTo(`/store/products/${serviceId}`)
}

/**
 * @description 清除搜索关键词
 */
const clearSearchKeyword = () => {
  searchKeyword.value = ''
  showThink.value = false
  clearSuggestions()
}

/**
 * @description 处理取消按钮点击（移动端）
 */
const handleCancel = () => {
  emit('update:visible', false)
}

/**
 * @description 完成搜索：活动模式 emit；商品模式跳转搜索结果页
 * @param {{ displayText: string, keyword: string }} payload
 */
const finishSearch = (payload) => {
  if (!payload?.displayText) {
    BaseMessage.warning('请输入关键词搜索')
    return
  }

  searchKeyword.value = payload.displayText
  addToSearchHistory(payload)

  if (isActivityMode.value) {
    emit('search', payload.displayText)
    emit('update:visible', false)
    return
  }

  navigateTo(`/store/search?q=${encodeURIComponent(payload.displayText)}`)
  emit('update:visible', false)
}

/**
 * @description 处理搜索提交（防抖，避免重复点击导致多次跳转）
 */
const handleSearchSubmit = useDebounceFn(
  () => {
    finishSearch(
      createStoreSearchPayload({
        displayText: searchKeyword.value,
        hotSearchList: hotSearchList.value,
      })
    )
  },
  300,
  { leading: true, trailing: false }
)

/**
 * @description 移动端右侧操作：未输入时取消搜索，输入后提交搜索
 */
const handleMobileAction = () => {
  if (isSearchKeywordEmpty.value) {
    handleCancel()
    return
  }

  handleSearchSubmit()
}

/**
 * @description 处理热门搜索点击
 */
const handleHotSearchClick = (item) => {
  const payload = createStoreSearchPayload({
    displayText: getHotSearchText(item),
    actualKeyword: getHotSearchContent(item),
    hotSearchList: hotSearchList.value,
  })
  if (!payload.keyword) return

  finishSearch(payload)
}

/**
 * @description 处理搜索历史点击
 */
const handleHistoryClick = (item) => {
  const payload = createStoreSearchPayload({
    displayText: getHistoryText(item),
    actualKeyword: getSearchHistoryKeyword(item),
    hotSearchList: hotSearchList.value,
  })
  if (!payload.keyword) return

  finishSearch(payload)
}

/**
 * @description 执行清除搜索历史逻辑
 */
const executeClearHistory = async () => {
  try {
    if (isActivityMode.value) {
      searchHistoryList.value = []
      if (import.meta.client) {
        localStorage.setItem(getActiveSearchHistoryKey(), JSON.stringify([]))
      }
      BaseMessage.success('清除成功')
      return
    }

    if (isLoggedIn.value) {
      await baseApi.deleteSearchKeywords()
    }
    searchHistoryList.value = []
    if (import.meta.client) {
      localStorage.setItem(getActiveSearchHistoryKey(), JSON.stringify([]))
    }
    BaseMessage.success('清除成功')
  } catch (error) {
    console.error('清除搜索历史失败:', error)
    searchHistoryList.value = []
    if (import.meta.client) {
      localStorage.setItem(getActiveSearchHistoryKey(), JSON.stringify([]))
    }
    BaseMessage.success('清除成功')
  }
}

/**
 * @description 一键清除搜索历史（带确认弹窗）
 * 移动端使用 Figma 设计稿样式，PC 端使用 CommonModal
 */
const handleClearHistory = () => {
  if (isMobile.value) {
    showClearConfirmModal.value = true
  } else {
    CommonModal.confirm({
      title: '确认清除',
      content: '确定要清除全部搜索历史吗？',
      type: 'warning',
      okText: '确定',
      cancelText: '取消',
      onOk: executeClearHistory,
    })
  }
}

/**
 * @description 移动端确认清除弹窗 - 确定
 */
const handleClearConfirmOk = async () => {
  await executeClearHistory()
  showClearConfirmModal.value = false
}

/**
 * @description 移动端确认清除弹窗 - 取消
 */
const handleClearConfirmCancel = () => {
  showClearConfirmModal.value = false
}

/**
 * @description 添加到搜索历史
 */
const addToSearchHistory = (historyItem) => {
  const payload =
    typeof historyItem === 'string'
      ? createStoreSearchPayload({ displayText: historyItem, hotSearchList: hotSearchList.value })
      : createStoreSearchPayload({
          displayText: historyItem?.displayText,
          actualKeyword: historyItem?.keyword,
          hotSearchList: hotSearchList.value,
        })

  if (!payload.displayText || !payload.keyword) return

  const normalizedItem = {
    displayText: payload.displayText,
    keyword: payload.keyword,
  }

  // 移除已存在的相同关键词
  const index = searchHistoryList.value.findIndex((item) => {
    return getSearchHistoryKeyword(item) === normalizedItem.keyword
  })
  if (index !== -1) {
    searchHistoryList.value.splice(index, 1)
  }

  // 添加到开头
  searchHistoryList.value.unshift(normalizedItem)

  // 限制历史记录数量（最多20条）
  if (searchHistoryList.value.length > 20) {
    searchHistoryList.value = searchHistoryList.value.slice(0, 20)
  }

  // 保存到本地存储
  if (import.meta.client) {
    localStorage.setItem(getActiveSearchHistoryKey(), JSON.stringify(searchHistoryList.value))
  }

  if (!isActivityMode.value && isLoggedIn.value) {
    baseApi.recordSearchKeyword(normalizedItem.keyword).catch((error) => {
      console.error('记录搜索历史失败:', error)
    })
  }
}

/**
 * @description 加载热门搜索
 */
const loadHotSearch = async () => {
  hotSearchLoading.value = true
  try {
    const response = await baseApi.getHotSearch(1)
    // axios拦截器已经处理了响应，直接返回data字段或整个响应
    // 如果返回的是数组，直接使用；如果是对象，取data字段
    const data = Array.isArray(response) ? response : response?.data || response || []
    // 限制为前30个
    hotSearchList.value = Array.isArray(data) ? data.slice(0, 30) : []
  } catch (error) {
    console.error('获取热门搜索失败:', error)
    hotSearchList.value = []
  } finally {
    hotSearchLoading.value = false
  }
}

/**
 * @description 加载搜索历史
 */
const loadSearchHistory = async () => {
  try {
    if (isActivityMode.value) {
      if (import.meta.client) {
        const localHistory = localStorage.getItem(getActiveSearchHistoryKey())
        searchHistoryList.value = mergeSearchHistoryLists(
          localHistory ? JSON.parse(localHistory) : []
        )
      } else {
        searchHistoryList.value = []
      }
      return
    }

    // 检查是否登录
    if (!isLoggedIn.value) {
      // 未登录时，从本地存储获取搜索历史
      if (import.meta.client) {
        const localHistory = localStorage.getItem(getActiveSearchHistoryKey())
        searchHistoryList.value = mergeSearchHistoryLists(
          localHistory ? JSON.parse(localHistory) : []
        )
      } else {
        searchHistoryList.value = []
      }
      return
    }

    const response = await baseApi.getSearchKeywords()
    // axios拦截器已经处理了响应，直接返回data字段或整个响应
    // 如果返回的是数组，直接使用；如果是对象，取data字段
    const data = Array.isArray(response)
      ? response
      : response?.list || response?.data || response || []
    const localHistory = import.meta.client
      ? JSON.parse(localStorage.getItem(getActiveSearchHistoryKey()) || '[]')
      : []
    searchHistoryList.value = mergeSearchHistoryLists(localHistory, Array.isArray(data) ? data : [])
    // 同步到本地存储
    if (import.meta.client) {
      localStorage.setItem(getActiveSearchHistoryKey(), JSON.stringify(searchHistoryList.value))
    }
  } catch (error) {
    console.error('获取搜索历史失败:', error)
    // 失败时从本地存储获取
    if (import.meta.client) {
      const localHistory = localStorage.getItem(getActiveSearchHistoryKey())
      searchHistoryList.value = mergeSearchHistoryLists(
        localHistory ? JSON.parse(localHistory) : []
      )
    } else {
      searchHistoryList.value = []
    }
  }
}

/**
 * @description 获取热门搜索显示文本（用于显示）
 */
const getHotSearchText = (item) => getHotSearchDisplayText(item)

/**
 * @description 获取热门搜索内容（用于实际搜索）
 */
const getHotSearchContent = (item) => getHotSearchKeyword(item)

/**
 * @description 获取历史记录文本
 */
const getHistoryText = (item) => getSearchHistoryDisplayText(item)

/**
 * @description 截断文本
 */
const truncateText = (text, maxLength) => {
  if (!text) return ''
  return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text
}

/**
 * @description 处理蒙版点击，关闭搜索面板
 */
const handleOverlayClick = () => {
  emit('update:visible', false)
}

/**
 * @description 面板打开后聚焦搜索框（移动端弹出键盘）
 */
const focusSearchInput = () => {
  if (!import.meta.client || !props.visible) return

  const applyFocus = () => {
    const instance = searchInputRef.value
    if (instance?.focus) {
      instance.focus()
      return
    }
    const inputEl = instance?.$el?.querySelector?.('input')
    inputEl?.focus?.()
  }

  nextTick(() => {
    applyFocus()
    window.setTimeout(applyFocus, 80)
  })
}

// 监听visible变化，加载数据
watch(
  [() => props.visible, isMobile],
  ([newVisible, newMobile]) => {
    if (newVisible) {
      bindSearchPanelScrollClose()
      // 获取header高度
      getHeaderHeight()

      // 并行加载热门搜索和历史记录，加载完成后检查溢出
      Promise.all([loadHotSearch(), loadSearchHistory()]).finally(() => {
        checkOverflow()
      })

      // 重置展开状态
      isHotSearchExpanded.value = false
      isHistoryExpanded.value = false
      // 重置输入框与联想态
      searchKeyword.value = String(props.initialKeyword || '').trim()
      showThink.value = false
      clearSuggestions()

      focusSearchInput()

      // 移动端：锁定背景滚动 (同时锁定 html 和 body)
      if (newMobile && import.meta.client) {
        document.documentElement.style.overflow = 'hidden'
        document.body.style.overflow = 'hidden'
        // 针对部分移动端浏览器，可能还需要锁定高度
        document.documentElement.style.height = '100%'
        document.body.style.height = '100%'
      } else if (import.meta.client) {
        // PC端或其他情况恢复滚动
        document.documentElement.style.overflow = ''
        document.body.style.overflow = ''
        document.documentElement.style.height = ''
        document.body.style.height = ''
      }
    } else {
      unbindSearchPanelScrollClose()
      // 恢复背景滚动
      if (import.meta.client) {
        document.documentElement.style.overflow = ''
        document.body.style.overflow = ''
        document.documentElement.style.height = ''
        document.body.style.height = ''
      }
    }
  },
  { immediate: true }
)

onMounted(() => {
  // 初始化时获取header高度
  getHeaderHeight()
  // 监听窗口大小变化，节流重新计算 header 高度
  if (import.meta.client) {
    window.addEventListener('resize', throttledGetHeaderHeight)
  }
})

onUnmounted(() => {
  if (import.meta.client) {
    window.removeEventListener('resize', throttledGetHeaderHeight)
    unbindSearchPanelScrollClose()
    // 确保组件销毁时恢复滚动
    document.documentElement.style.overflow = ''
    document.body.style.overflow = ''
    document.documentElement.style.height = ''
    document.body.style.height = ''
  }
})
</script>

<style scoped lang="scss">
/* 遮罩层 */
.search-panel-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba($color-black, 0.6);
  z-index: 999; // 低于header的1000，确保header在蒙版上方
  display: flex;
  align-items: flex-start;
  justify-content: center;
  cursor: pointer; // 添加指针样式，提示可点击
  // padding-top通过内联样式动态设置，紧贴header底部
  box-sizing: border-box;
}

/* 搜索面板容器 */
.search-panel-container {
  width: 100%;
  max-width: 100%;
  background-color: $bg-white; // Figma设计：背景白色
  min-height: 360px;
  box-shadow: none;
  overflow: hidden;
  animation: slideDown 0.3s ease-out;
  box-sizing: border-box;
  position: relative;

  // 小于900px时才添加padding
  @media (max-width: 939px) {
    padding: 0 16px;
  }
  @include respond-to-max('lg') {
    padding: 0 6px 40px; // 添加底部内边距，防止内容被遮挡
    height: 120%;
    min-height: 0;
    overflow-y: auto; // 移动端允许内部滚动
    -webkit-overflow-scrolling: touch; // 优化 iOS 滚动流畅度
    overscroll-behavior: contain; // 防止滚动链触发背景滚动
  }
}

.search-panel__close-btn {
  position: absolute;
  top: 32px;
  right: 32px;
  width: 20px;
  height: 20px;
  padding: 0;
  border: none;
  background-color: transparent;
  color: $text-title-color;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  transition: color 0.2s ease;

  &:hover {
    color: $primary-color;
  }
}

/* 动画 */
.search-panel-fade-enter-active,
.search-panel-fade-leave-active {
  transition: opacity 0.3s ease;
}

.search-panel-fade-enter-from,
.search-panel-fade-leave-to {
  opacity: 0;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 搜索输入框区域 */
.search-panel__input-area {
  padding: 32px 0; // 上下间距，左右由 wrapper 控制

  // 移动端：添加左右 padding
  @include respond-to-max('lg') {
    padding: 24px 16px;
  }
}

.search-panel__input-wrapper {
  display: flex;
  gap: 0;
  align-items: stretch; // 输入框与按钮等高，参照 store/search
  max-width: 900px; // 最大宽度 900px
  margin: 0 auto; // 居中显示
}

.search-panel__input-container {
  flex: 1;
  position: relative;
  max-width: 820px; // Figma设计：输入框宽度820px
  display: flex;
  min-width: 0;
}

.search-panel__back-btn {
  display: inline-flex;
  flex: 0 0 20px;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  border: 0;
  background: transparent;
  color: $text-title-color;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
}

.search-panel__input {
  flex: 1;
  min-width: 0;
  border-radius: 4px 0 0 4px;

  :deep(.arco-input) {
    border-right: none;
    height: 100%;
    font-size: 16px;
    background-color: transparent;
    color: $text-strong-color; // Figma设计：文字颜色#19191A
  }

  :deep(.arco-input-wrapper) {
    height: 56px;
    box-sizing: border-box;
    border-radius: 4px 0 0 4px;
    background-color: $bg-color;
    border: 1px solid $border-muted-color;
    border-right: none;

    &:hover {
      background-color: $bg-color;
    }

    &.arco-input-focus {
      background-color: $bg-color;
      border-color: $primary-color;
      box-shadow: none;
      outline: none;
    }
  }

  :deep(.arco-input) {
    &:focus {
      outline: none;
    }
  }

  // 移动端样式（根据 Figma 设计）
  &--mobile {
    :deep(.arco-input) {
      height: 100%;
      font-size: 14px;
      color: $text-title-color;
    }

    :deep(.arco-input-wrapper) {
      height: 36px; // Figma设计：36px
      box-sizing: border-box;
      border-radius: 6px;
      background-color: $bg-color; // Figma设计：#F7F7F7
      border: 1px solid transparent;
      padding-left: 12px; // Figma设计：搜索图标左边距12px

      &:hover {
        background-color: $bg-color;
        border-color: transparent;
      }

      &.arco-input-focus {
        background-color: $bg-color;
        border-color: $primary-color;
        box-shadow: none;
        outline: none;
      }
    }

    :deep(.arco-input::placeholder) {
      color: $text-stat-zero-color; // Figma设计：占位符颜色$text-stat-zero-color
      font-size: 14px;
    }
  }
}

.search-panel__input.arco-input-wrapper {
  height: 56px;
  box-sizing: border-box;
  background-color: $bg-color;
  border: 1px solid $border-muted-color;
  border-right: none;
  border-radius: 4px 0 0 4px;

  &.arco-input-focus {
    background-color: $bg-color;
    border-color: $primary-color;
    box-shadow: none;
    outline: none;
  }
}

.search-panel__input--mobile.arco-input-wrapper {
  height: 36px;
  background-color: $bg-color;
  border: 1px solid transparent;
  border-radius: 6px;
  padding-left: 12px;

  &:hover {
    background-color: $bg-color;
    border-color: transparent;
  }

  &.arco-input-focus {
    background-color: $bg-color;
    border-color: $primary-color;
    box-shadow: none;
    outline: none;
  }
}

.search-panel__search-icon {
  width: 16px;
  height: 16px;
  font-size: 16px;
  color: $text-content-color;
  flex-shrink: 0;
}

.search-panel__clear-icon {
  cursor: pointer;
  color: $text-content-color;
  transition: all 0.2s ease;
  width: 36px;
  height: 36px;
  border-radius: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  padding: 8px;

  &:hover {
    color: $text-title-color;
    background-color: rgba($color-black, 0.06);
  }

  &:active {
    background-color: rgba($color-black, 0.1);
  }
}

.search-panel__clear-icon--mobile {
  width: 16px;
  height: 16px;
  padding: 3px;
  border-radius: 50%;
  font-size: 10px;
  color: $bg-white;
  background-color: $text-caption-color;

  &:hover,
  &:active {
    color: $bg-white;
    background-color: $text-caption-color;
  }
}

.search-panel__cancel-btn {
  height: 56px;
  min-width: 60px;
  flex-shrink: 0;
  padding: 0 16px;
  font-size: 16px;
  color: $primary-color;
  border: none;
  background-color: transparent;

  &:hover {
    color: $primary-color-hover;
    background-color: transparent;
  }

  @include respond-to-max('lg') {
    height: 36px;
  }
}

.search-panel__search-btn {
  height: 56px;
  width: 80px; // Figma设计：按钮宽度80px
  flex-shrink: 0;
  border-radius: 0 4px 4px 0;
  padding: 0;
  background-color: $primary-color; // Figma设计：主色
  border-color: $primary-color;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: $primary-color-hover;
    border-color: $primary-color-hover;
  }
}

/* 内容区域 */
.search-panel__section {
  padding: 0 0 32px; // Figma设计：调整间距
  max-width: 900px; // 最大宽度 900px，与输入框区域保持一致
  margin: 0 auto; // 居中显示
}

.search-panel__section-title {
  font-size: 16px; // Figma设计：16px
  font-weight: 500;
  color: $text-title-color; // Figma设计：#202427
  line-height: 24px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.search-panel__section-title-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.search-panel__expand-btn-small {
  font-size: 14px;
  color: $text-subtitle-color;
  cursor: pointer;
  transition: color 0.2s;
  @media (hover: hover) and (pointer: fine) {
    &:hover {
      color: $primary-color;
    }
  }
}

.search-panel__clear-all {
  font-size: 16px;
  color: $text-content-color;
  cursor: pointer;
  transition: color 0.2s;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: $primary-color;
  }
}

.search-panel__tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px; // Figma设计：标签之间的间距为8px
  max-height: 72px; // 32px height * 2 + 8px gap
  overflow: hidden;
  transition: max-height 0.3s ease;

  &--expanded {
    max-height: none;
    overflow: visible;
  }
}

.search-panel__tags :deep(.search-tag) {
  height: 32px;
  padding: 5px 12px;
  font-size: 14px;
  line-height: 22px;
  border-color: $border-subtle-color;
}

/* 骨架屏样式 */
.search-panel__skeleton {
  margin-bottom: 0;
}

.search-panel__skeleton-title {
  width: 80px;
  height: 24px;
  background: linear-gradient(90deg, $bg-color 25%, rgba($bg-color, 0.5) 50%, $bg-color 75%);
  background-size: 200% 100%;
  border-radius: 4px;
  margin-bottom: 12px;
  animation: search-panel-skeleton-loading 1.5s ease-in-out infinite;
}

.search-panel__skeleton-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.search-panel__skeleton-tag {
  width: 80px;
  height: 32px;
  background: linear-gradient(90deg, $bg-color 25%, rgba($bg-color, 0.5) 50%, $bg-color 75%);
  background-size: 200% 100%;
  border-radius: 32px;
  animation: search-panel-skeleton-loading 1.5s ease-in-out infinite;

  &:nth-child(1) {
    width: 100px;
  }
  &:nth-child(2) {
    width: 120px;
  }
  &:nth-child(3) {
    width: 90px;
  }
  &:nth-child(4) {
    width: 110px;
  }
  &:nth-child(5) {
    width: 85px;
  }
  &:nth-child(6) {
    width: 95px;
  }
}

@keyframes search-panel-skeleton-loading {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* 响应式 */
@include respond-to-max('lg') {
  // padding-top 由内联样式动态设置；移动端 navbar 高度见 $base-header-height-mobile

  .search-panel__input-area {
    padding: 12px 10px;
  }

  .search-panel__section {
    padding: 0 10px 24px;
  }

  // 小屏下保持一行显示，参考搜索页面实现
  .search-panel__input-wrapper {
    // 保持 flex 横向布局，不改为 column
    flex-direction: row;
    align-items: center;
    gap: 0;
    width: 100%;
    max-width: 100%;
  }

  .search-panel__input-container {
    flex: 1;
    max-width: 100%;
    margin-right: 8px; // 与取消按钮之间的间距
  }

  .search-panel__cancel-btn {
    height: 36px; // 与输入框高度一致
    min-width: 42px;
    padding: 0 12px;
    font-size: 14px;
    color: $text-title-color;
    flex-shrink: 0;
    border-radius: 0;
    background-color: transparent;

    &:hover {
      color: $text-title-color;
      background-color: transparent;
    }

    &--submit {
      color: $primary-color;

      &:hover {
        color: $primary-color;
      }
    }
  }
}

/* 搜索联想列表 */
.search-panel__think-list {
  background-color: $bg-white;
}

.search-panel__think-tip {
  padding: 16px 0;
  font-size: 14px;
  color: $text-subtitle-color;

  &--loading {
    display: flex;
    align-items: center;
    gap: 8px;
  }
}

.think-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
  cursor: pointer;
  transition: color 0.2s;
  border-bottom: 1px solid $border-light-color;

  &:last-child {
    border-bottom: none;
  }

  &__icon {
    flex-shrink: 0;
    color: $text-subtitle-color;
    font-size: 16px;
  }

  &__text {
    font-size: 16px;
    color: $text-title-color;
    @include text-ellipsis(1);
  }

  &:hover {
    .think-item__text,
    .think-item__icon {
      color: $primary-color;
    }
  }
}

/* 移动端清除搜索历史确认弹窗 - Figma 设计稿样式 */
.search-panel-clear-confirm-modal {
  :deep(.arco-modal-wrap),
  :deep(.arco-modal) {
    border-radius: 8px;
    overflow: hidden;
    max-width: min(270px, calc(100vw - 24px));
    background-color: $bg-white;
  }

  :deep(.arco-modal-body) {
    padding: 0;
    background-color: $bg-white;
  }
}

.search-panel-clear-confirm-modal__content {
  min-height: 89px;
  padding: 24px 16px;
  font-size: 17px;
  font-weight: 500;
  line-height: 24px;
  color: $text-title-color;
  text-align: center;
  background-color: $bg-white;
  border-bottom: 1px solid $border-light-color;
}

.search-panel-clear-confirm-modal__footer {
  display: flex;
  padding: 0;
  background-color: $bg-white;
}

.search-panel-clear-confirm-modal__btn {
  flex: 1;
  height: 44px;
  font-size: 16px;
  font-weight: 400;
  line-height: 22px;
  border: none;
  border-radius: 0;
  cursor: pointer;
  color: $primary-color;
  background-color: $bg-white;
  transition: background-color 0.2s;

  &--cancel {
    &:active {
      background-color: $bg-color;
    }
  }

  &--ok {
    border-left: 1px solid $border-light-color;

    &:active {
      background-color: $bg-color;
    }
  }
}
</style>
